import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { logoutTawkChat } from '@/lib/tawk';
import type { User, Session, UserAttributes, AuthMFAListFactorsResponse, Factor } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  mfaRequired: boolean;
  clearMfaRequired: () => void;
  activeTotp: Factor | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (
    email: string,
    password: string,
    marketingConsent?: boolean,
    redirectPath?: string,
    signupSource?: string,
  ) => Promise<{ error: string | null; confirmEmail: boolean }>;
  signInWithGoogle: (redirectPath?: string) => Promise<void>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  updateDisplayName: (name: string) => Promise<{ error: string | null }>;
  updateAvatarUrl: (url: string) => Promise<{ error: string | null }>;
  updateUser: (user: UserAttributes) => Promise<{ error: string | null }>;
  getFactors: () => Promise<AuthMFAListFactorsResponse['data']>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [activeTotp, setActiveTotp] = useState<Factor | null>(null);

  async function checkMfaRequired() {
    const { data: assurance } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    setMfaRequired(assurance?.nextLevel === 'aal2' && assurance?.currentLevel !== 'aal2');
  }

  useEffect(() => {
    // Flag to stop onAuthStateChange from racing with the initial async MFA check
    let initialCheckDone = false;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session) {
        await checkMfaRequired();
      }

      initialCheckDone = true;
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);

      // Wait for the async initial check above to finish before acting on events
      if (!initialCheckDone) {
        return;
      }

      if (event === 'SIGNED_IN' && session) {
        // Deferred: supabase holds an internal lock while notifying subscribers,
        // so auth calls made directly in this callback can deadlock
        setTimeout(() => void checkMfaRequired(), 0);
      }

      if (event === 'MFA_CHALLENGE_VERIFIED') {
        setMfaRequired(false);
      }

      if (event === 'SIGNED_OUT') {
        setMfaRequired(false);
        logoutTawkChat();
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const totp = user?.factors?.find((f) => f.factor_type === 'totp' && f.status === 'verified');

    if (totp) {
      setActiveTotp(totp);
    }
  }, [user]);

  async function getFactors(): Promise<AuthMFAListFactorsResponse['data']> {
    const { data: factors } = await supabase.auth.mfa.listFactors();

    if (user) {
      setUser({
        ...user,
        factors: factors?.all,
      });
    }

    return factors;
  }

  function clearMfaRequired() {
    setMfaRequired(false);
  }

  async function signIn(email: string, password: string): Promise<{ error: string | null }> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      if (error.message.includes('Invalid login credentials')) {
        return { error: 'Feil e-post eller passord.' };
      }

      if (error.message.includes('Email not confirmed')) {
        return { error: 'Bekreft e-posten din før du logger inn.' };
      }

      return { error: 'Noe gikk galt. Prøv igjen.' };
    }

    // Set mfaRequired before resolving so the route guards redirect to /login/mfa
    await checkMfaRequired();

    return { error: null };
  }

  async function signUp(
    email: string,
    password: string,
    marketingConsent = false,
    redirectPath?: string,
    signupSource?: string,
  ): Promise<{ error: string | null; confirmEmail: boolean }> {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: window.location.origin + (redirectPath ?? ''),
        data: {
          terms_accepted: true,
          marketing_consent: marketingConsent,
          marketing_consent_date: marketingConsent ? new Date().toISOString() : null,
          ...(signupSource && { signup_source: signupSource }),
        },
      },
    });

    // With email enumeration protection, an existing email returns a fake user with no identities
    if (error?.message.includes('already registered') || data.user?.identities?.length === 0) {
      return { error: 'Denne e-posten er allerede registrert.', confirmEmail: false };
    }

    if (error) {
      return { error: 'Noe gikk galt. Prøv igjen.', confirmEmail: false };
    }

    return { error: null, confirmEmail: !data.session };
  }

  async function signInWithGoogle(redirectPath?: string) {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + (redirectPath ?? ''),
      },
    });
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  async function updatePassword(newPassword: string): Promise<{ error: string | null }> {
    return updateUser({ password: newPassword });
  }

  async function updateDisplayName(name: string): Promise<{ error: string | null }> {
    return updateUser({ data: { full_name: name } });
  }

  async function updateAvatarUrl(url: string): Promise<{ error: string | null }> {
    return updateUser({ data: { avatar_url: url } });
  }

  async function updateUser(user: UserAttributes): Promise<{ error: string | null }> {
    const { error } = await supabase.auth.updateUser(user);

    if (error) {
      console.error('Could not update user', error);
      return { error: 'Noe gikk galt. Prøv igjen.' };
    }

    return { error: null };
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        mfaRequired,
        activeTotp,
        getFactors,
        clearMfaRequired,
        signIn,
        signUp,
        signInWithGoogle,
        signOut,
        updatePassword,
        updateDisplayName,
        updateAvatarUrl,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
